<template>
  <v-navigation-drawer
    :value="navigationDrawerVisibility"
    :width="sidenavWidth"
    app
    :temporary="isMobile"
    fixed
    @input="switchNavigationDrawerVisibility"
    class="nav-drawer"
  >
    <!-- Barre latérale du design system (SideNav) : marque, métier, entrées groupées, utilisateur en pied -->
    <nav class="hb-nav nav" aria-label="Principal">
      <div class="hb-nav-brand nav__brand">
        <img :src="contextLogo" alt="" class="nav__logo" :class="{ 'nav__logo--boucherie': app === 'carnot' }" />
        <span class="nav__brand-name">Hublot</span>
        <span class="nav__brand-tag">CRM</span>
      </div>

      <!-- Métier : Poissonnerie / Boucherie -->
      <div class="nav__seg" role="group" aria-label="Métier">
        <button
          v-for="option in contextOptions"
          :key="option.value"
          type="button"
          :aria-pressed="String(app === option.value)"
          @click="setContext(option.value)"
        >{{ option.label }}</button>
      </div>

      <!-- Sections ouvertes par défaut, repliables par une petite flèche (repli mémorisé) ;
           la section de la page courante reste toujours ouverte -->
      <div class="hb-nav-sections">
        <div v-for="group in navGroups" :key="group.label || 'root'" class="hb-nav-section">
          <button
            v-if="group.label"
            type="button"
            class="hb-nav-group"
            :aria-expanded="String(!isCollapsed(group))"
            @click="toggleGroup(group.label)"
          >
            <span>{{ group.label }}</span>
            <v-icon class="chev" size="14">chevron_right</v-icon>
          </button>
          <v-expand-transition>
            <div v-show="!isCollapsed(group)" class="hb-nav-items">
              <router-link
                v-for="item in group.items"
                :key="item.to"
                :to="item.to"
                custom
                v-slot="{ href, navigate }"
              >
                <a
                  :href="href"
                  :aria-current="isActive(item.to) ? 'page' : null"
                  @click="onNavigate($event, navigate)"
                >
                  <v-icon class="nav__icon" size="18">{{ item.icon }}</v-icon>
                  <span>{{ item.title }}</span>
                </a>
              </router-link>
            </div>
          </v-expand-transition>
        </div>
      </div>

    </nav>
  </v-navigation-drawer>
</template>

<script>
  import { mapState } from 'vuex'
  import navigationDrawerBoucherie from '@/assets/definitions/navigationDrawerBoucherie.json';
  import navigationDrawerPoissonnerie from '@/assets/definitions/navigationDrawerPoissonnerie.json';
  import InputComponentMixin from '../mixins/InputComponentMixin'
  import { applyActivity } from '@/plugins/theme';

  const COLLAPSED_GROUPS_KEY = 'crm-nav-collapsed-groups'
  const readCollapsedGroups = () => {
    try {
      return JSON.parse(localStorage.getItem(COLLAPSED_GROUPS_KEY)) || {}
    } catch (error) {
      return {}
    }
  }

  export default {
    data () {
      return {
        customer: {},
        drawer: false,
        collapsedGroups: readCollapsedGroups(), // libellés des sections repliées par l'utilisateur
        contextOptions: [
          { value: 'hublot', label: 'Poissonnerie', logo: require('../assets/poissonnerie.png') },
          { value: 'carnot', label: 'Boucherie', logo: require('../assets/boucherie.png') }
        ]
      }
    },
    mixins: [InputComponentMixin],
    methods: {
      switchNavigationDrawerVisibility (state) {
        this.$store.dispatch('setNavigationDrawerVisibility', state)
      },
      // Navigue via le router (ctrl/cmd-clic garde l'ouverture dans un nouvel onglet)
      // et ferme le menu sur mobile (drawer temporaire)
      onNavigate (event, navigate) {
        navigate(event)
        if (this.isMobile) {
          this.$store.dispatch('setNavigationDrawerVisibility', false)
        }
      },
      // « / » n'est actif que sur l'accueil ; les autres entrées couvrent leurs sous-routes
      isActive (to) {
        const currentPath = this.$route.path
        if (to === '/') return currentPath === '/'
        return currentPath === to || currentPath.startsWith(to + '/')
      },
      // Une section repliée se rouvre d'elle-même dès qu'elle contient la page courante
      isCollapsed (group) {
        return !!group.label && !!this.collapsedGroups[group.label] && !group.items.some(item => this.isActive(item.to))
      },
      toggleGroup (label) {
        this.collapsedGroups = { ...this.collapsedGroups, [label]: !this.collapsedGroups[label] }
        try {
          localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify(this.collapsedGroups))
        } catch (error) {
          // stockage local indisponible (navigation privée) : le repli ne survit pas au rechargement
        }
      },
      setContext (app) {
        if (app === this.app) return
        applyActivity(this.$vuetify, app)

        // Vérifier si la route courante existe dans le nouveau contexte
        const newDrawer = app === 'carnot' ? navigationDrawerBoucherie : navigationDrawerPoissonnerie
        const availableRoutes = newDrawer.reduce((routes, item) => {
          if (item.to) routes.push(item.to)
          if (item.children) item.children.map(child => child.to).filter(Boolean).forEach(r => routes.push(r))
          return routes
        }, [])
        const currentPath = this.$route.path
        const routeExists = availableRoutes.some(route => currentPath === route || currentPath.startsWith(route + '/'))

        // Rediriger d'abord si la route n'existe pas, pour démonter le composant
        // avant que le changement de contexte ne déclenche de nouveaux appels API
        if (!routeExists) {
          this.$router.push('/').then(() => this.$store.dispatch('setApp', app))
        } else {
          this.$store.dispatch('setApp', app)
        }
      }
    },
    computed: {
      sidenavWidth () {
        return parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sidenav'), 10) || 224
      },
      // Entrées à plat sous des libellés de groupe (hb-nav-group) : les éléments simples
      // en tête forment un groupe sans libellé, chaque section devient un groupe
      navGroups () {
        return this.currentDrawer.reduce((groups, item) => {
          if (item.isSection) return groups.concat({ label: item.title, items: item.children || [] })
          const last = groups[groups.length - 1]
          if (last && !last.label) {
            last.items.push(item)
            return groups
          }
          return groups.concat({ label: null, items: [item] })
        }, [])
      },
      currentDrawer(){
        if(this.app === 'carnot')
          return navigationDrawerBoucherie
        else
          return navigationDrawerPoissonnerie
      },
      isMobile () {
        // Sous 960 px (seuil mobile de Vuetify) : menu en superposition, ouvert par le hamburger
        return this.$vuetify.breakpoint.mobile
      },
      contextLogo() {
        return this.app === 'hublot'
          ? require('../assets/poissonnerie.png')
          : require('../assets/boucherie.png');
      },
      contextLabel() {
        return this.app === 'hublot' ? 'Poissonnerie' : 'Boucherie';
      },
      ...mapState({
        app : state => state.home.app,
        navigationDrawerVisibility: state => state.home.navigationDrawerVisibility,
        backgroundcolor: state => state.home.color,
        customers: state => state.customers.list
      })
    },
    watch: {
      app(newVal) {
        this.setContext(newVal)
      }
    },
    mounted() {
      // Métier restauré depuis le stockage local ; sur petit écran le menu démarre replié
      applyActivity(this.$vuetify, this.app)
      if (this.isMobile) this.$store.dispatch('setNavigationDrawerVisibility', false)
    }
  }
</script>

<style lang="scss" scoped>
/* La présentation vient de .hb-nav (hublot.css) ; ici, seulement l'intégration Vuetify
   et les éléments propres au CRM (logo métier, sélecteur de métier, pied utilisateur). */

::v-deep .v-navigation-drawer__content {
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--nav-ink) 30%, transparent) transparent;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: color-mix(in srgb, var(--nav-ink) 30%, transparent); border-radius: 3px; }
}

.nav {
  width: 100%;
  text-align: left; // annule le text-align:center global (#app)

  // .v-application a (design-system.scss) passe après hublot.css : on reprend la main
  a { color: var(--nav-muted); }
  a[aria-current="page"] { color: var(--nav-active); }
  .hb-nav-group { color: var(--nav-muted); }
  @media (hover: hover) and (pointer: fine) {
    a:hover, .hb-nav-group:hover { color: var(--nav-ink); }
  }
}

.nav__icon.v-icon { color: inherit; opacity: .7; }
a[aria-current="page"] .nav__icon.v-icon { color: var(--nav-active); opacity: 1; }

/* ───────── Marque ───────── */
.nav__brand { gap: 10px; }
.nav__logo {
  width: 28px;
  height: 28px;
  flex: none;
  border-radius: var(--radius-full);
  object-fit: cover;
  object-position: top;
}
.nav__logo--boucherie { background: var(--surface-raised); }
.nav__brand-name { font-family: var(--font-display); font-weight: 400; font-size: 22px; letter-spacing: 0; color: var(--nav-ink); }
.nav__brand-tag {
  margin-left: auto;
  font: var(--text-label);
  color: var(--nav-muted);
}

/* ───────── Métier ───────── */
.nav__seg {
  display: flex;
  gap: 2px;
  margin: 0;
  padding: 3px;
  border-radius: var(--radius-md);
  background: var(--surface-raised);
  border: 1px solid var(--line);

  button {
    flex: 1;
    height: 28px;
    padding: 0 6px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--ink-muted);
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 120ms ease, color 120ms ease;

    &[aria-pressed="true"] { background: var(--brand-soft); color: var(--brand); }
    &:focus-visible { outline: 2px solid var(--focus-ring); outline-offset: 2px; }
  }
  @media (hover: hover) and (pointer: fine) {
    button:hover { color: var(--ink); }
  }
}

</style>
